// src/components/ui/StepImaginaryHandwashingBubble.tsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ImaginaryHandwashingArtifactConfig } from "../../types/chat";

type Props = {
  artifact: ImaginaryHandwashingArtifactConfig;
  sessionId?: string;
  accessCode?: string;
  onFinished?: () => void;
};

type Stage = "idle" | "started" | "washing" | "completed" | "failed";

type HandPosition = {
  x: number;
  y: number;
};

type GameObject = {
  id: string;
  label: string;
  icon: string;
  x: string;
  y: string;
  width?: string;
};

type GameResult = {
  points: number;
  timeScore: number;
  soapScore: number;
  elapsedSeconds: number;
  soapPumps: number;
  touchedCount: number;
  message: string;
};

const DEFAULT_OBJECT_LAYOUT: Array<Pick<GameObject, "x" | "y" | "width">> = [
  { x: "7%", y: "3%", width: "90px" },    // Book
  { x: "54%", y: "5%", width: "90px" },  // Pen
  { x: "72%", y: "28%", width: "90px" },  // Cloth
  { x: "2%", y: "33%", width: "90px" },   // Door knob
  { x: "49%", y: "50%", width: "90px" },  // Shoe
  { x: "8%", y: "64%", width: "90px" },  // Sock
  { x: "70%", y: "59%", width: "90px" },  // Restroom
];

const DEFAULT_OBJECTS: GameObject[] = [
  { id: "book", label: "Book", icon: "📘", ...DEFAULT_OBJECT_LAYOUT[0] },
  { id: "pen", label: "Pen", icon: "🖊️", ...DEFAULT_OBJECT_LAYOUT[1] },
  { id: "cloth", label: "Cloth", icon: "👕", ...DEFAULT_OBJECT_LAYOUT[2] },
  { id: "doorknob", label: "Door knob", icon: "🚪", ...DEFAULT_OBJECT_LAYOUT[3] },
  { id: "shoe", label: "Shoe", icon: "👠", ...DEFAULT_OBJECT_LAYOUT[4] },
  { id: "sock", label: "Sock", icon: "🧦", ...DEFAULT_OBJECT_LAYOUT[5] },
  { id: "restroom", label: "Restroom", icon: "🚻", ...DEFAULT_OBJECT_LAYOUT[6] },
];

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function iconForObject(icon?: string, label?: string) {
  const key = String(icon || label || "").toLowerCase();

  if (key.includes("book")) return "📘";
  if (key.includes("pen")) return "🖊️";
  if (key.includes("cloth") || key.includes("shirt")) return "👕";
  if (key.includes("door")) return "🚪";
  if (key.includes("shoe")) return "👠";
  if (key.includes("sock")) return "🧦";
  if (key.includes("restroom") || key.includes("bathroom")) return "🚻";

  return "🧼";
}

function FaucetGraphic({ running }: { running: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center">
      <svg
        width="86"
        height="60"
        viewBox="0 0 86 60"
        xmlns="http://www.w3.org/2000/svg"
        className="block overflow-visible drop-shadow-sm"
        aria-hidden="true"
      >
        <defs>
          <linearGradient
            id="ihwFaucetBody"
            x1="12"
            y1="8"
            x2="72"
            y2="50"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#94A3B8" />
            <stop offset="0.55" stopColor="#64748B" />
            <stop offset="1" stopColor="#475569" />
          </linearGradient>
          <linearGradient
            id="ihwFaucetHighlight"
            x1="26"
            y1="12"
            x2="62"
            y2="30"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#F8FAFC" stopOpacity="0.75" />
            <stop offset="1" stopColor="#CBD5E1" stopOpacity="0" />
          </linearGradient>
        </defs>

        <rect x="35" y="3" width="16" height="6" rx="3" fill="#64748B" />
        <rect x="41" y="8" width="4" height="10" rx="2" fill="#64748B" />
        <rect x="10" y="22" width="23" height="9" rx="4.5" fill="url(#ihwFaucetBody)" />
        <path
          d="M30 25h25c9.5 0 17.5 8 17.5 17.5v2.5h-15v-3.5c0-3.1-2.4-5.5-5.5-5.5H38c-6 0-11-5-11-11v-2h3z"
          fill="url(#ihwFaucetBody)"
        />
        <rect x="59" y="42" width="10" height="15" rx="5" fill="url(#ihwFaucetBody)" />
        <path
          d="M32 25h24c5 0 9 2.2 11.8 5.8"
          stroke="url(#ihwFaucetHighlight)"
          strokeWidth="4"
          strokeLinecap="round"
        />
      </svg>

      <div className="mt-1 flex h-8 items-start justify-center gap-1.5">
        {running ? (
          <>
            <span className="ihw-water-stream h-5 w-2 rounded-full bg-sky-400 shadow-sm" />
            <span className="ihw-water-stream ihw-water-stream-delay-1 h-7 w-2 rounded-full bg-sky-400 shadow-sm" />
            <span className="ihw-water-stream ihw-water-stream-delay-2 h-5 w-2 rounded-full bg-sky-400 shadow-sm" />
          </>
        ) : (
          <span className="mt-2 h-2 w-2 rounded-full bg-slate-300" />
        )}
      </div>
    </div>
  );
}

function TwoHandsGraphic({
  washing,
  dragging,
}: {
  washing: boolean;
  dragging?: boolean;
}) {
  return (
    <div
      className={`flex h-24 w-32 items-center justify-center rounded-full border border-white/80 bg-white shadow-xl transition-transform ${washing ? "ihw-hands-washing" : ""
        } ${dragging ? "scale-105" : ""}`}
    >
      <div className="relative flex items-center justify-center">
        <span
          className={`text-5xl ${washing ? "ihw-left-hand-washing" : ""}`}
          style={{ transform: "scaleX(-1) rotate(-6deg)" }}
        >
          🖐️
        </span>
        <span className={`-ml-2 text-5xl ${washing ? "ihw-right-hand-washing" : ""}`}>
          🖐️
        </span>
      </div>
    </div>
  );
}

function SoapGraphic({ used }: { used: number }) {
  return (
    <div className="relative flex h-full w-full items-center justify-center">
      <div className="absolute inset-3 rounded-[1.2rem] bg-emerald-100/65" />
      <div className={`relative flex flex-col items-center ${used > 0 ? "ihw-soap-bump" : ""}`}>
        <div className="mb-1 h-2 w-6 rounded-t-lg bg-rose-300" />
        <div className="relative h-16 w-10 rounded-[1rem] border border-emerald-200 bg-gradient-to-b from-white to-emerald-50 shadow-md">
          <div className="absolute left-1/2 top-2 h-5 w-5 -translate-x-1/2 rounded-full bg-emerald-100" />
          <div className="absolute bottom-2 left-1/2 h-4 w-6 -translate-x-1/2 rounded-full bg-emerald-200/70" />
        </div>
      </div>
    </div>
  );
}

function ScorePill({
  label,
  value,
  tone = "slate",
}: {
  label: string;
  value: string;
  tone?: "slate" | "blue" | "emerald" | "amber";
}) {
  const toneClass =
    tone === "blue"
      ? "bg-sky-50 text-sky-700 border-sky-200"
      : tone === "emerald"
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : tone === "amber"
          ? "bg-amber-50 text-amber-700 border-amber-200"
          : "bg-slate-50 text-slate-700 border-slate-200";

  return (
    <div className={`rounded-2xl border px-3 py-2 shadow-sm ${toneClass}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide opacity-75">
        {label}
      </div>
      <div className="text-lg font-bold leading-tight">{value}</div>
    </div>
  );
}

export default function StepImaginaryHandwashingBubble({
  artifact,
  sessionId,
  accessCode,
  onFinished,
}: Props) {
  void sessionId;
  void accessCode;

  const config = artifact.config || {};

  const maxSeconds = config.maxSeconds ?? 20;
  const idealSeconds = Math.min(config.idealSeconds ?? 20, maxSeconds);
  const maxSoapPumps = config.maxSoapPumps ?? 5;
  const idealSoapPumps = config.idealSoapPumps ?? 1;
  const requiredTouchCount = config.requiredTouchCount ?? 3;
  const basePoints = config.basePoints ?? 10;
  const hardZeroAtMaxSoap = config.scoring?.hardZeroAtMaxSoap ?? true;
  const hardZeroAtMaxSeconds = config.scoring?.hardZeroAtMaxSeconds ?? true;

  const objects: GameObject[] = useMemo(() => {
    const incoming =
      Array.isArray(config.objects) && config.objects.length > 0
        ? config.objects
        : DEFAULT_OBJECTS;

    return incoming.map((obj, index) => {
      const layout = DEFAULT_OBJECT_LAYOUT[index % DEFAULT_OBJECT_LAYOUT.length];

      return {
        id: obj.id || `object-${index + 1}`,
        label: obj.label || `Object ${index + 1}`,
        icon: iconForObject(obj.icon, obj.label),
        x: layout.x,
        y: layout.y,
        width: layout.width,
      };
    });
  }, [config.objects]);

  const [stage, setStage] = useState<Stage>("idle");
  const [touchedIds, setTouchedIds] = useState<string[]>([]);
  const [soapPumps, setSoapPumps] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [result, setResult] = useState<GameResult | null>(null);
  const [faucetOn, setFaucetOn] = useState(false);
  const [hasFaucetStarted, setHasFaucetStarted] = useState(false);
  const [handPos, setHandPos] = useState<HandPosition>({ x: 420, y: 525 });
  const [isDraggingHands, setIsDraggingHands] = useState(false);
  const [finishedInserted, setFinishedInserted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [portalReady, setPortalReady] = useState(false);

  const intervalRef = useRef<number | null>(null);
  const touchedIdsRef = useRef<string[]>([]);
  const soapPumpsRef = useRef(0);

  const playAreaRef = useRef<HTMLDivElement | null>(null);
  const expandedPlayAreaRef = useRef<HTMLDivElement | null>(null);

  const homeHandsRef = useRef<HTMLDivElement | null>(null);
  const expandedHomeHandsRef = useRef<HTMLDivElement | null>(null);

  const embeddedObjectRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const expandedObjectRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const dragOffsetRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    setPortalReady(true);
  }, []);

  useEffect(() => {
    touchedIdsRef.current = touchedIds;
  }, [touchedIds]);

  useEffect(() => {
    soapPumpsRef.current = soapPumps;
  }, [soapPumps]);

  useEffect(() => {
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, []);

  const remainingSeconds = Math.max(0, maxSeconds - elapsedSeconds);
  const touchedCount = touchedIds.length;
  const isFinished = stage === "completed" || stage === "failed";

  function clearTimer() {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }

  function calculateScore(
    elapsed: number,
    pumps: number,
    touched: number,
    faucetStarted: boolean
  ): GameResult {
    if (!faucetStarted) {
      return {
        points: 0,
        timeScore: 0,
        soapScore: 0,
        elapsedSeconds: elapsed,
        soapPumps: pumps,
        touchedCount: touched,
        message: "Try again: turn on the faucet before finishing the handwashing routine.",
      };
    }

    if (touched < requiredTouchCount) {
      return {
        points: 0,
        timeScore: 0,
        soapScore: 0,
        elapsedSeconds: elapsed,
        soapPumps: pumps,
        touchedCount: touched,
        message: `Try again: touch at least ${requiredTouchCount} contaminated objects before washing.`,
      };
    }

    if (hardZeroAtMaxSoap && pumps >= maxSoapPumps) {
      return {
        points: 0,
        timeScore: 0,
        soapScore: 0,
        elapsedSeconds: elapsed,
        soapPumps: pumps,
        touchedCount: touched,
        message: `You used all ${maxSoapPumps} soap pumps. That drops the score to 0. Try using just one pump next time.`,
      };
    }

    if (hardZeroAtMaxSeconds && elapsed >= maxSeconds) {
      return {
        points: 0,
        timeScore: 0,
        soapScore: 0,
        elapsedSeconds: elapsed,
        soapPumps: pumps,
        touchedCount: touched,
        message: `The ${maxSeconds}-second timer ran out. That drops the score to 0. Try stopping sooner next time.`,
      };
    }

    const halfPoints = basePoints / 2;

    const timeScore =
      elapsed <= idealSeconds
        ? halfPoints
        : halfPoints * ((maxSeconds - elapsed) / Math.max(1, maxSeconds - idealSeconds));

    const soapScore =
      pumps <= idealSoapPumps
        ? halfPoints
        : halfPoints * ((maxSoapPumps - pumps) / Math.max(1, maxSoapPumps - idealSoapPumps));

    const points = clamp(Math.round(timeScore + soapScore), 0, basePoints);

    let message = `Completed: ${pumps} soap pump${pumps === 1 ? "" : "s"}, ${elapsed} seconds, ${points}/${basePoints} points.`;

    if (points === basePoints) {
      message = `Excellent ERP practice: you touched contaminated objects, used one pump, stopped within ${idealSeconds} seconds, and earned ${basePoints}/${basePoints} points.`;
    } else if (points >= Math.ceil(basePoints * 0.7)) {
      message += " Nice work. You stopped before the safety behavior took over.";
    } else {
      message += " Good practice. Next round, aim for fewer pumps and a shorter wash.";
    }

    return {
      points,
      timeScore,
      soapScore,
      elapsedSeconds: elapsed,
      soapPumps: pumps,
      touchedCount: touched,
      message,
    };
  }

  const pointsPreview = useMemo(() => {
    if (isFinished) return result?.points ?? 0;
    if (stage === "idle") return basePoints;

    return calculateScore(
      Math.min(elapsedSeconds, maxSeconds - 1),
      soapPumps,
      Math.max(touchedIds.length, requiredTouchCount),
      hasFaucetStarted || stage === "washing"
    ).points;
  }, [
    basePoints,
    elapsedSeconds,
    hasFaucetStarted,
    isFinished,
    maxSeconds,
    requiredTouchCount,
    result,
    soapPumps,
    stage,
    touchedIds.length,
  ]);

  function getActivePlayArea() {
    return expanded ? expandedPlayAreaRef.current : playAreaRef.current;
  }

  function getActiveHomeHands() {
    return expanded ? expandedHomeHandsRef.current : homeHandsRef.current;
  }

  function getActiveObjectRefs() {
    return expanded ? expandedObjectRefs.current : embeddedObjectRefs.current;
  }

  function resetHandPosition() {
    const area = getActivePlayArea()?.getBoundingClientRect();
    const home = getActiveHomeHands()?.getBoundingClientRect();

    if (area && home) {
      setHandPos({
        x: home.left + home.width / 2 - area.left,
        y: home.top + home.height / 2 - area.top,
      });
      return;
    }

    setHandPos({ x: 420, y: 525 });
  }

  function resetGame() {
    clearTimer();
    setStage("idle");
    setTouchedIds([]);
    setSoapPumps(0);
    setElapsedSeconds(0);
    setResult(null);
    setFaucetOn(false);
    setHasFaucetStarted(false);
    setIsDraggingHands(false);
    setFinishedInserted(false);
    window.setTimeout(resetHandPosition, 50);
  }

  function startGame() {
    clearTimer();
    resetHandPosition();
    setStage("started");
    setTouchedIds([]);
    setSoapPumps(0);
    setElapsedSeconds(0);
    setResult(null);
    setFaucetOn(false);
    setHasFaucetStarted(false);
    setIsDraggingHands(false);
    setFinishedInserted(false);
  }

  function touchObject(id: string) {
    if (stage !== "started") return;
    setTouchedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }

  function checkObjectTouchAtHandCenter(localX: number, localY: number) {
    if (stage !== "started") return;

    const area = getActivePlayArea()?.getBoundingClientRect();
    if (!area) return;

    const handCenterClientX = area.left + localX;
    const handCenterClientY = area.top + localY;

    const refs = getActiveObjectRefs();

    for (const object of objects) {
      const node = refs[object.id];
      if (!node) continue;

      const rect = node.getBoundingClientRect();

      const isInside =
        handCenterClientX >= rect.left &&
        handCenterClientX <= rect.right &&
        handCenterClientY >= rect.top &&
        handCenterClientY <= rect.bottom;

      if (isInside) {
        touchObject(object.id);
      }
    }
  }

  function handleHandsPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (stage !== "started") return;

    const area = getActivePlayArea()?.getBoundingClientRect();
    if (!area) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);

    dragOffsetRef.current = {
      x: event.clientX - (area.left + handPos.x),
      y: event.clientY - (area.top + handPos.y),
    };

    setIsDraggingHands(true);
    checkObjectTouchAtHandCenter(handPos.x, handPos.y);
  }

  function handleHandsPointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!isDraggingHands || stage !== "started") return;

    const area = getActivePlayArea()?.getBoundingClientRect();
    if (!area) return;

    event.preventDefault();

    const nextX = clamp(
      event.clientX - area.left - dragOffsetRef.current.x,
      72,
      area.width - 72
    );

    const nextY = clamp(
      event.clientY - area.top - dragOffsetRef.current.y,
      72,
      area.height - 72
    );

    setHandPos({ x: nextX, y: nextY });
    checkObjectTouchAtHandCenter(nextX, nextY);
  }

  function handleHandsPointerUp(event: React.PointerEvent<HTMLDivElement>) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsDraggingHands(false);
  }

  function pumpSoap() {
    if (stage !== "started" && stage !== "washing") return;
    setSoapPumps((prev) => Math.min(maxSoapPumps, prev + 1));
  }

  function toggleFaucet() {
    if (stage === "idle" || isFinished) return;

    setFaucetOn((prev) => !prev);

    if (!hasFaucetStarted) {
      setHasFaucetStarted(true);
      setStage("washing");
    }

    if (intervalRef.current === null) {
      intervalRef.current = window.setInterval(() => {
        setElapsedSeconds((prev) => {
          const next = prev + 1;

          if (next >= maxSeconds) {
            clearTimer();
            setFaucetOn(false);

            const finalResult = calculateScore(
              maxSeconds,
              soapPumpsRef.current,
              touchedIdsRef.current.length,
              true
            );

            setResult(finalResult);
            setStage("failed");
            return maxSeconds;
          }

          return next;
        });
      }, 1000);
    }
  }

  function finishGame() {
    if (stage !== "started" && stage !== "washing") return;

    clearTimer();
    setFaucetOn(false);

    const finalResult = calculateScore(
      elapsedSeconds,
      soapPumpsRef.current,
      touchedIdsRef.current.length,
      hasFaucetStarted
    );

    setResult(finalResult);
    setStage(finalResult.points === 0 ? "failed" : "completed");
  }

  function insertFinished() {
    if (finishedInserted) return;
    setFinishedInserted(true);
    onFinished?.();
  }

  const soapDots = Array.from({ length: maxSoapPumps }, (_, index) => index < soapPumps);

  const renderGame = (isExpanded: boolean) => {
    const playRef = isExpanded ? expandedPlayAreaRef : playAreaRef;
    const homeRef = isExpanded ? expandedHomeHandsRef : homeHandsRef;

    return (
      <div className="rounded-[1.6rem] border border-white bg-white/95 shadow-lg">
        <div className="border-b border-slate-100 p-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xl">✨</span>
                <h2 className="text-lg font-semibold text-slate-950">
                  {artifact.title || "20-Second Handwashing Challenge"}
                </h2>
              </div>
              <p className="mt-1 text-sm text-slate-600">
                Drag the hands over contaminated objects, use one pump, turn on the faucet, and stop within {idealSeconds} seconds.
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <ScorePill label="Time" value={`${remainingSeconds}s`} tone="blue" />
                <ScorePill label="Soap" value={`${soapPumps}/${maxSoapPumps}`} tone="emerald" />
                <ScorePill label="Objects" value={`${touchedCount}/${requiredTouchCount}`} tone="amber" />
                <ScorePill label="Points" value={`${pointsPreview}/${basePoints}`} />
              </div>

              {!isExpanded && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setExpanded(true);
                    window.setTimeout(resetHandPosition, 50);
                  }}
                  className="rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  ⛶ Expand
                </button>
              )}
            </div>
          </div>
        </div>

        <div className={isExpanded ? "grid gap-4 p-4 xl:grid-cols-[1fr_280px]" : "grid gap-4 p-4"}>
          <div
            ref={playRef}
            className="relative touch-none select-none rounded-[1.5rem] border border-white bg-gradient-to-br from-white to-slate-100 p-4 shadow-sm"
          >
            {stage === "started" && (
              <div
                className="pointer-events-auto absolute z-50 cursor-grab touch-none active:cursor-grabbing"
                style={{ left: handPos.x, top: handPos.y, transform: "translate(-50%, -50%)" }}
                onPointerDown={handleHandsPointerDown}
                onPointerMove={handleHandsPointerMove}
                onPointerUp={handleHandsPointerUp}
                onPointerCancel={handleHandsPointerUp}
              >
                <TwoHandsGraphic washing={false} dragging={isDraggingHands} />
              </div>
            )}

            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="font-medium text-slate-950">Practice area</h3>
                <p className="text-sm text-slate-600">
                  {stage === "started"
                    ? `Drag the hands over at least ${requiredTouchCount} contaminated objects. Then use soap and faucet.`
                    : "Start the challenge to move the hands and touch contaminated objects."}
                </p>
              </div>

              <div className="rounded-full border border-slate-200 bg-white px-3 py-1 text-sm font-medium text-slate-700 shadow-sm">
                {stage === "idle"
                  ? "Ready"
                  : stage === "started"
                    ? "Drag hands"
                    : stage === "washing"
                      ? faucetOn ? "Water on" : "Water off"
                      : stage === "completed"
                        ? "Complete"
                        : "Try again"}
              </div>
            </div>

            <div className={isExpanded ? "grid gap-4 lg:grid-cols-[1.05fr_1fr]" : "grid gap-4"}>
              <div className="rounded-[1.5rem] border border-slate-100 bg-white p-4 shadow-sm">
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Contaminated objects
                </h3>

                <div className="relative min-h-[455px] overflow-hidden rounded-[1.5rem] border border-dashed border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4">                  {objects.map((object) => {
                  const touched = touchedIds.includes(object.id);

                  return (
                    <button
                      key={object.id}
                      ref={(node: HTMLButtonElement | null) => {
                        if (isExpanded) {
                          expandedObjectRefs.current[object.id] = node;
                        } else {
                          embeddedObjectRefs.current[object.id] = node;
                        }
                      }}
                      type="button"
                      onClick={() => touchObject(object.id)}
                      disabled={stage !== "started"}
                      className={`absolute rounded-[1.25rem] border p-3 text-left shadow-sm transition ${touched
                        ? "border-emerald-300 bg-emerald-50 ring-2 ring-emerald-200"
                        : "border-slate-200 bg-white hover:-translate-y-0.5 hover:shadow-md"
                        } ${stage !== "started" ? "cursor-not-allowed opacity-70" : ""}`}
                      style={{ left: object.x, top: object.y, width: object.width ?? "110px" }}
                    >
                      <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-50 text-2xl shadow-sm">
                        {object.icon}
                      </div>
                      <div className="text-sm font-medium leading-tight text-slate-950">
                        {object.label}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        {touched ? "Touched" : "Drag hands here"}
                      </div>
                    </button>
                  );
                })}
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-slate-100 bg-white p-4 shadow-sm">
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Sink area
                </h3>

                <div className="relative min-h-[460px] overflow-hidden rounded-[2rem] bg-gradient-to-br from-slate-100 to-slate-200 p-4 shadow-inner">
                  <div className="absolute inset-x-5 top-12 h-[245px] rounded-[52px] border-[7px] border-slate-300 bg-gradient-to-b from-slate-50 to-white shadow-inner" />
                  <div className="absolute inset-x-14 top-[112px] h-32 rounded-full bg-sky-100/70 blur-sm" />

                  {faucetOn && (
                    <div className="ihw-water-glow absolute inset-x-20 top-[150px] h-20 rounded-full bg-sky-200/70 blur-md" />
                  )}

                  <div className="absolute left-1/2 top-3 z-40 -translate-x-1/2">
                    <button
                      type="button"
                      disabled={stage === "idle" || isFinished}
                      onClick={toggleFaucet}
                      className="flex h-[120px] w-[170px] items-center justify-center rounded-[2rem] border border-slate-200 bg-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60"
                      aria-label={faucetOn ? "Turn faucet off" : "Turn faucet on"}
                    >
                      <FaucetGraphic running={faucetOn} />
                    </button>
                  </div>

                  {stage !== "started" && (
                    <div
                      ref={homeRef}
                      className={`absolute left-1/2 -translate-x-1/2 ${stage === "washing" ? "top-[162px]" : "top-[318px]"
                        }`}
                    >
                      <TwoHandsGraphic washing={faucetOn} />
                    </div>
                  )}

                  <div className="absolute right-6 top-[126px] z-40">
                    <button
                      type="button"
                      disabled={stage === "idle" || isFinished}
                      onClick={pumpSoap}
                      className="flex h-[110px] w-[108px] items-center justify-center rounded-[1.7rem] border border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60"
                      aria-label="Pump soap dispenser"
                    >
                      <SoapGraphic used={soapPumps} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="rounded-[1.5rem] border border-slate-100 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-950">Controls</h3>

              <div className="mt-3 grid gap-2">
                {stage === "idle" ? (
                  <button
                    type="button"
                    className="rounded-2xl bg-slate-950 px-4 py-2.5 font-medium text-white shadow-sm transition hover:bg-slate-800"
                    onClick={startGame}
                  >
                    ▶️ Start challenge
                  </button>
                ) : (
                  <button
                    type="button"
                    className="rounded-2xl bg-slate-950 px-4 py-2.5 font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
                    onClick={finishGame}
                    disabled={isFinished}
                  >
                    ✅ I’m done washing
                  </button>
                )}

                <button
                  type="button"
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 font-medium text-slate-900 shadow-sm transition hover:bg-slate-50"
                  onClick={resetGame}
                >
                  ↻ Reset
                </button>
              </div>
            </div>

            <div className="rounded-[1.5rem] border border-slate-100 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-950">Soap tracker</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {soapDots.map((used, index) => (
                  <div
                    key={index}
                    className={`h-9 w-9 rounded-full border shadow-sm transition ${used
                      ? "border-emerald-300 bg-emerald-400"
                      : "border-slate-200 bg-slate-100"
                      }`}
                  />
                ))}
              </div>
              <p className="mt-3 text-sm text-slate-600">
                Use one pump for the best score. {maxSoapPumps} pumps drops the score to zero.
              </p>
            </div>

            <div className="rounded-[1.5rem] border border-slate-100 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-950">Scoring</h3>
              <div className="mt-3 space-y-2 text-sm text-slate-600">
                <p>{basePoints}/{basePoints} = one pump and finishing within {idealSeconds} seconds.</p>
                <p>At {maxSeconds} seconds, the timer runs out and the score drops to zero.</p>
                <p>Extra soap pumps gradually reduce points.</p>
              </div>
            </div>

            {result && (
              <div className="rounded-[1.5rem] border border-slate-100 bg-white p-4 shadow-sm">
                <h3 className="font-medium text-slate-950">Result</h3>
                <div className="mt-2 text-3xl font-semibold text-slate-950">
                  {result.points}/{basePoints}
                </div>
                <p className="mt-2 text-sm text-slate-700">{result.message}</p>

                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500">
                  <div>Time score: {result.timeScore.toFixed(1)}</div>
                  <div>Soap score: {result.soapScore.toFixed(1)}</div>
                  <div>Seconds: {result.elapsedSeconds}</div>
                  <div>Soap pumps: {result.soapPumps}</div>
                </div>

                <button
                  type="button"
                  className="mt-4 w-full rounded-2xl bg-slate-950 px-4 py-2.5 font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
                  onClick={insertFinished}
                  disabled={finishedInserted}
                >
                  {finishedInserted ? "Finished inserted" : "Mark homework step finished"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>
        {`
          @keyframes ihwWaterStream {
            0%, 100% { height: 12px; opacity: 0.72; }
            50% { height: 28px; opacity: 1; }
          }

          @keyframes ihwHandsWash {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-5px); }
          }

          @keyframes ihwLeftHandWash {
            0%, 100% { transform: scaleX(-1) rotate(-8deg); }
            50% { transform: scaleX(-1) rotate(-2deg); }
          }

          @keyframes ihwRightHandWash {
            0%, 100% { transform: rotate(8deg); }
            50% { transform: rotate(2deg); }
          }

          @keyframes ihwSoapBump {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
          }

          @keyframes ihwWaterGlow {
            0%, 100% { opacity: 0.25; }
            50% { opacity: 0.55; }
          }

          .ihw-water-stream {
            animation: ihwWaterStream 0.75s infinite;
          }

          .ihw-water-stream-delay-1 {
            animation-delay: 0.1s;
          }

          .ihw-water-stream-delay-2 {
            animation-delay: 0.2s;
          }

          .ihw-hands-washing {
            animation: ihwHandsWash 1.05s infinite;
          }

          .ihw-left-hand-washing {
            animation: ihwLeftHandWash 0.95s infinite;
          }

          .ihw-right-hand-washing {
            animation: ihwRightHandWash 0.95s 0.08s infinite;
          }

          .ihw-soap-bump {
            animation: ihwSoapBump 0.35s ease;
          }

          .ihw-water-glow {
            animation: ihwWaterGlow 1.4s infinite;
          }
        `}
      </style>

      <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-3 shadow-sm">
        {renderGame(false)}
      </div>

      {portalReady && expanded
        ? createPortal(
          <div
            className="fixed inset-0 z-[99999] bg-slate-950/60 p-3 backdrop-blur-sm sm:p-5"
            role="dialog"
            aria-modal="true"
          >
            <div className="mx-auto flex h-full max-w-7xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
              <div className="flex items-center justify-between gap-3 border-b border-slate-200 p-4">
                <div>
                  <div className="text-lg font-semibold text-slate-950">
                    {artifact.title || "20-Second Handwashing Challenge"}
                  </div>
                  <div className="text-sm text-slate-600">Expanded practice view</div>
                </div>

                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setExpanded(false);
                    window.setTimeout(resetHandPosition, 50);
                  }}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  Close
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-auto bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-4">
                {renderGame(true)}
              </div>
            </div>
          </div>,
          document.body
        )
        : null}
    </>
  );
}